import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getActivityId } from '../utils/autoLayout';
import { getActivityTitle } from '../utils/boardSelectionContext';
import { extractTextShapeContent } from '../utils/canvasTextContent';
import { dedupeBlockers } from '../utils/linkBlockersToActions';
import {
  buildSettledStepNotes,
  settledNoteDetail,
} from '../utils/settledStepNotes';
import { useGeminiLanguage } from '../hooks/useGeminiLanguage';
import { getCanvasMessages } from '../utils/canvasI18n';
import { finishedSteps } from '../utils/steps';
import { useBoardStore } from '../store/useBoardStore';

const STORAGE_KEY = 'hashely.canvas.settledExpanded';

function readExpanded() {
  try {
    const stored = window.localStorage?.getItem(STORAGE_KEY);
    if (stored === '0' || stored === 'false') return false;
    if (stored === '1' || stored === 'true') return true;
  } catch {
    // ignore storage errors
  }
  return true;
}

function blockerSettledKey(blocker, index) {
  const title = String(blocker?.title || '').trim();
  return title || `blocker:${index}`;
}

function settledShapeTitle(shape, activityTitle = '') {
  const fromActivity = String(activityTitle || '').trim();
  if (fromActivity) return fromActivity.slice(0, 80);
  if (!shape) return '';
  if (shape.type === 'frame') {
    return String(shape.props?.name || '').trim().slice(0, 80);
  }
  if (shape.type === 'text' || shape.type === 'note') {
    const line = extractTextShapeContent(shape)
      .split('\n')
      .map(part => part.trim())
      .find(Boolean) || '';
    return line.slice(0, 80);
  }
  return String(shape.props?.title || shape.meta?.title || '').trim().slice(0, 80);
}

function resolveSettledTarget(editor, {
  activityId = '',
  shapeId = '',
} = {}, activities = []) {
  if (!editor) return null;
  const aid = String(activityId || '').trim();
  const sid = String(shapeId || '').trim();
  let shape = null;
  if (aid) {
    const desired = `shape:pin-${aid}`;
    shape = editor.getShape?.(desired) || null;
    if (!shape) {
      shape = editor.getCurrentPageShapes?.().find(
        item => (item.type === 'pin-card' || item.type === 'suggest-pin')
          && String(item.meta?.activityId) === aid
      ) || null;
    }
  } else if (sid) {
    shape = editor.getShape?.(sid) || null;
  }
  if (!shape?.id) return null;
  const activity = aid
    ? (activities || []).find(item => String(getActivityId(item)) === aid)
    : null;
  return {
    id: String(shape.id),
    title: settledShapeTitle(shape, activity ? getActivityTitle(activity) : ''),
  };
}

function doneKeysForStep(blockers, userProgress) {
  const keys = new Set(
    (Array.isArray(userProgress?.doneBlockerKeys) ? userProgress.doneBlockerKeys : [])
      .map(key => String(key || '').trim())
      .filter(Boolean)
  );
  const fills = Array.isArray(userProgress?.doneFills) ? userProgress.doneFills : [];
  for (const fill of fills) {
    const title = String(fill?.doneKey || '').trim().toLowerCase();
    if (!title) continue;
    blockers.forEach((blocker, index) => {
      const key = blockerSettledKey(blocker, index);
      if (key.toLowerCase() === title || String(blocker?.title || '').trim().toLowerCase() === title) {
        keys.add(key);
      }
    });
  }
  return keys;
}

export default function BoardSettledPanel({
  editorRef,
  activities,
  board,
  contextActions,
}) {
  const rootRef = useRef(null);
  const { geminiLanguage } = useGeminiLanguage();
  const messages = getCanvasMessages(geminiLanguage);
  const canvasEpistemicNonce = useBoardStore(state => state.canvasEpistemicNonce || 0);
  const [expanded, setExpanded] = useState(readExpanded);

  const blockers = useMemo(() => {
    const raw = Array.isArray(contextActions?.blockers) && contextActions.blockers.length
      ? contextActions.blockers
      : (contextActions?.gaps || [])
        .map(gap => (
          typeof gap === 'string'
            ? { title: gap, why: '', howToFill: '' }
            : gap
        ))
        .filter(item => item?.title);
    return dedupeBlockers(raw);
  }, [contextActions?.blockers, contextActions?.gaps]);

  const doneSteps = useMemo(() => finishedSteps(board), [board]);

  const notes = useMemo(() => {
    const progress = contextActions?.userProgress || {};
    return buildSettledStepNotes({
      finishedSteps: doneSteps,
      blockers,
      doneKeys: doneKeysForStep(blockers, progress),
      notInterestedKeys: progress.notInterestedKeys,
      doneFills: progress.doneFills,
      constraints: board?.contextRecommendations?.constraints || contextActions?.constraints,
      resolveTarget: (ref) => resolveSettledTarget(
        canvasEpistemicNonce >= 0 ? editorRef?.current : null,
        ref,
        activities,
      ),
    });
  }, [
    doneSteps,
    blockers,
    contextActions?.userProgress,
    contextActions?.constraints,
    board?.contextRecommendations?.constraints,
    activities,
    editorRef,
    canvasEpistemicNonce,
  ]);

  useEffect(() => {
    const node = rootRef.current;
    const stage = node?.closest?.('.board-canvas-stage');
    if (!node || !stage) return undefined;
    const apply = () => {
      stage.style.setProperty('--board-settled-offset', `${node.offsetHeight + 12}px`);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(node);
    return () => {
      observer.disconnect();
      stage.style.removeProperty('--board-settled-offset');
    };
  }, [expanded, notes.length]);

  const toggleExpanded = useCallback(() => {
    setExpanded(prev => {
      const next = !prev;
      try {
        window.localStorage?.setItem(STORAGE_KEY, next ? '1' : '0');
      } catch {
        // ignore storage errors
      }
      return next;
    });
  }, []);

  const selectNote = useCallback((note) => {
    const editor = editorRef?.current;
    const shapeIds = (note?.shapeIds || []).filter(id => editor?.getShape?.(id));
    if (!editor || !shapeIds.length) return;
    editor.setSelectedShapes(shapeIds);
    try {
      editor.zoomToSelection?.({ animation: { duration: 200 } });
    } catch {
      // ignore camera helpers missing in tests
    }
  }, [editorRef]);

  return (
    <Box ref={rootRef} className="board-settled-panel">
      <button
        type="button"
        className="board-settled-panel-toggle"
        aria-expanded={expanded}
        onClick={toggleExpanded}
      >
        <ExpandMoreIcon
          className={[
            'board-settled-panel-chevron',
            expanded ? 'is-expanded' : '',
          ].filter(Boolean).join(' ')}
          fontSize="small"
        />
        <span className="board-settled-panel-title">{messages.settledSoFar}</span>
        {notes.length ? (
          <span className="board-settled-panel-count">{notes.length}</span>
        ) : null}
      </button>
      {expanded ? (
        <Box className="board-settled-panel-body">
          {notes.length ? notes.map((note, index) => {
            const detail = settledNoteDetail(note, {
              evidenceCount: messages.settledEvidenceCount,
              gone: messages.settledGone,
            });
            const aria = detail ? `${note.label}. ${detail}` : note.label;
            const startsGroup = index === 0 || notes[index - 1].source !== note.source;
            return (
              <React.Fragment key={note.id}>
                {startsGroup ? (
                  <span className="board-settled-panel-group">
                    {note.source === 'board' ? messages.settledFromBoard : messages.settledFromSolved}
                  </span>
                ) : null}
                <button
                  type="button"
                  className={[
                    'board-ai-settled-note',
                    note.source === 'solved' ? 'board-ai-settled-note-solved' : '',
                    !note.missing && !note.shapeIds.length ? 'board-ai-settled-note-static' : '',
                  ].filter(Boolean).join(' ')}
                  disabled={note.missing || !note.shapeIds.length}
                  aria-label={aria}
                  title={aria}
                  onClick={() => selectNote(note)}
                >
                  <span className="board-ai-settled-label">
                    <CheckIcon className="board-ai-settled-check" fontSize="inherit" />
                    <span className="board-ai-settled-label-text">{note.label}</span>
                  </span>
                  {detail ? (
                    <span className="board-ai-settled-detail">{detail}</span>
                  ) : null}
                </button>
              </React.Fragment>
            );
          }) : (
            <Typography variant="caption" className="board-settled-panel-empty" color="text.secondary">
              {messages.settledEmpty}
            </Typography>
          )}
        </Box>
      ) : null}
    </Box>
  );
}
